/**
 * The agency-level areas beside Clients: Templates, Settings, and the area
 * still being built (MCP). Data lives in ./data; Reports and Roll-Ups in ./rollups.
 */

import { useEffect, useState, type ReactNode } from 'react';

import type { Branding } from '../portal/portal-api.ts';
import { agencyApi } from '../reports/agency-api.ts';
import { BrandingDialog } from '../reports/BrandingDialog.tsx';
import type { Template } from '../reports/reports-api.ts';
import { NotificationSettings } from '../kpis/NotificationSettings.tsx';
import { WHITE_LABEL_ON } from '../whitelabel/switch.ts';
import { WhiteLabelSettings } from '../whitelabel/WhiteLabelSettings.tsx';

export function Page({
  title,
  children,
  actions,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  return (
    <section className="ag-page" aria-labelledby="ag-area-title">
      <header className="ag-head">
        <h1 id="ag-area-title">{title}</h1>
        <span className="ag-spacer" />
        {actions}
      </header>
      <div className="ag-body">{children}</div>
    </section>
  );
}

export function AgencyTemplates({ templates }: { templates: Template[] }): ReactNode {
  const group = (dashboards: boolean) =>
    templates.filter((template) => template.kinds.includes('DASHBOARD') === dashboards);
  return (
    <Page title="Templates">
      {[true, false].map((dashboards) => (
        <div key={String(dashboards)} className="ag-table-card">
          <p className="ag-table-meta">{dashboards ? 'Dashboard templates' : 'Report templates'}</p>
          <table className="ag-table">
            <thead>
              <tr>
                <th scope="col">Template</th>
                <th scope="col">Pages</th>
              </tr>
            </thead>
            <tbody>
              {group(dashboards).map((template) => (
                <tr key={template.key}>
                  <td>
                    <strong>{template.name}</strong>
                    <small className="ag-cell-note">{template.description}</small>
                  </td>
                  <td>{template.pages.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </Page>
  );
}

export function AgencySettings({ organisationId }: { organisationId: string }): ReactNode {
  const [branding, setBranding] = useState<Branding | null>(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    agencyApi
      .branding(organisationId)
      .then(setBranding)
      .catch(() => setBranding(null));
  }, [organisationId]);
  return (
    <Page title="Settings">
      <section className="card ag-settings">
        <h2>Branding</h2>
        <p className="panel-note">
          Your agency's name, colour and logo on every report cover and in the client portal.
        </p>
        <p>
          <strong>{branding?.display_name ?? '…'}</strong>
        </p>
        <button type="button" className="btn secondary" onClick={() => setEditing(true)}>
          Edit branding
        </button>
      </section>
      {WHITE_LABEL_ON && <WhiteLabelSettings organisationId={organisationId} />}
      <NotificationSettings organisationId={organisationId} />
      <section className="card ag-settings">
        <h2>Security</h2>
        <p className="panel-note">
          Two-step verification is required on every account, for your team and your clients.
        </p>
      </section>
      {editing && (
        <BrandingDialog
          organisationId={organisationId}
          onClose={() => setEditing(false)}
          onSaved={setBranding}
        />
      )}
    </Page>
  );
}
